import axios from 'axios';
import { Endpoint } from 'types/url';
import { buildSearchParams } from 'utils/url';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  DismissFindingRequest,
  DismissFindingResponse,
  ExportFindingsParams,
  GetFindingResponse,
  GetFindingsFiltersResponse,
  GetFindingsInsightsResponse,
  GetFindingsParams,
  GetFindingsResponse,
  GetImpactReportResponse,
  GetPersonaFindingsStatsParams,
  GetPersonaFindingsStatsResponse,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosFindingsInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosFindingsInstance);

export class FindingsClient extends BaseHttpClient {
  constructor() {
    super(axiosFindingsInstance);
  }

  static override instance = new FindingsClient();

  /**
   * Get paginated list of findings
   */
  static async getFindings(
    params?: GetFindingsParams,
  ): Promise<GetFindingsResponse> {
    const searchParams = buildSearchParams(params);

    return await FindingsClient.get<GetFindingsResponse>(
      '/findings' as Endpoint,
      {
        params: searchParams,
      },
    );
  }

  /**
   * Get findings insights metrics
   */
  static async getInsights(): Promise<GetFindingsInsightsResponse> {
    return await FindingsClient.get<GetFindingsInsightsResponse>(
      '/findings/insights' as Endpoint,
    );
  }

  /**
   * Get the analytics impact report. Aggregated server-side over a fixed
   * window, so it takes no parameters.
   */
  static async getImpactReport(): Promise<GetImpactReportResponse> {
    return await FindingsClient.get<GetImpactReportResponse>(
      '/findings/impact-report' as Endpoint,
    );
  }

  /**
   * Get available filter values for findings
   */
  static async getFindingsFilters(): Promise<GetFindingsFiltersResponse> {
    return await FindingsClient.get<GetFindingsFiltersResponse>(
      '/findings/filters' as Endpoint,
    );
  }

  /**
   * Get bulk per-profile findings stats over a rolling window. Returns one
   * map keyed by personaIdentifier so the Team Profiles catalog hydrates
   * every row in a single request (no per-row fetch / N+1).
   */
  static async getPersonaFindingsStats(
    params?: GetPersonaFindingsStatsParams,
  ): Promise<GetPersonaFindingsStatsResponse> {
    const searchParams = buildSearchParams(params);

    return await FindingsClient.get<GetPersonaFindingsStatsResponse>(
      '/findings/persona-stats' as Endpoint,
      { params: searchParams },
    );
  }

  /**
   * Get a single finding by ID
   */
  static async getFinding(findingId: string): Promise<GetFindingResponse> {
    return await FindingsClient.get<GetFindingResponse>(
      `/findings/${findingId}`,
    );
  }

  static async dismissFinding(
    findingId: string,
    request: DismissFindingRequest,
  ): Promise<DismissFindingResponse> {
    return await FindingsClient.post<DismissFindingResponse>(
      `/findings/${findingId}/dismiss`,
      request,
    );
  }

  /**
   * Export findings to CSV. Capped at 10,000 rows on the server.
   * Accepts the same filters/sort as the list endpoint.
   */
  static async exportFindings(params?: ExportFindingsParams): Promise<Blob> {
    const searchParams = buildSearchParams(params);

    return await FindingsClient.get<Blob>('/findings/export' as Endpoint, {
      params: searchParams,
      responseType: 'blob',
      extractNestedData: false,
    });
  }
}
